<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import dayjs, { Dayjs } from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

import ErrorModal from '@/components/common/ErrorModal.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SparkBreadcrumb from '@/components/generic/SparkBreadcrumb.vue'
import { ROUTE_NAMES } from '@/router/route-names'
import { useMainStore } from '@/stores'
import { useAccessStore } from '@/stores/access'
import { components } from '#/types/capi-generated'

import { generateId } from './utils'

const dataTypeOptions = [
  {
    text: 'Regas Costs',
    value: 'regas-costs',
    description: 'Terminal regasification costs',
    trialHiddenFields: ['fuelGasLossesGasInKind', 'entryCapacity', 'entryVariable', 'emissions', 'power', 'portCost', 'totalWithPortCost']
  },
  {
    text: 'DES Hub Netbacks',
    value: 'des-hub-netbacks',
    description: 'DES Hub netback prices',
    trialHiddenFields: ['fuelGasLossesGasInKind', 'entryCapacity', 'entryVariable', 'emissions', 'power']
  }
]

const vesselSizeOptions: { text: string; value: components['schemas']['RegasCostsVesselSize'] }[] = [
  {
    text: '160',
    value: '160000'
  },
  {
    text: '174',
    value: '174000'
  }
]

const unitOptions: { text: string; value: components['schemas']['RegasCostsUnit'] }[] = [
  {
    text: '$/MMBtu',
    value: 'usd-per-mmbtu'
  },
  {
    text: '€/MWh',
    value: 'eur-per-mwh'
  }
]

const priceReleasesOptions = [
  {
    text: 'Latest',
    value: 'latest'
  },
  {
    text: 'Date Range',
    value: 'date-range'
  }
]

const startDate = ref<Dayjs>(dayjs().subtract(7, 'day'))
const endDate = ref<Dayjs>(dayjs())

const state = reactive<{
  checkedList: string[]
  dataType: string
  vesselSize: components['schemas']['RegasCostsVesselSize']
  unit: components['schemas']['RegasCostsUnit']
  priceReleasesFilter: string
  submitting: boolean
  errors: string[]
  showErrorModal: boolean
}>({
  checkedList: [] as string[],
  dataType: 'regas-costs',
  vesselSize: '174000',
  unit: 'usd-per-mmbtu',
  priceReleasesFilter: 'latest',
  submitting: false,
  errors: [] as string[],
  showErrorModal: false
})

const accessStore = useAccessStore()
const mainStore = useMainStore()

// Performance optimization: Move constants outside function to avoid recreation
const MAX_CELLS_PER_OPERATION = 1000000 // 1M cells as safe limit

async function loadData() {
  state.submitting = true
  // excelClearTable()

  state.errors = []
  let columns: string[] = []
  let rows: string[][] = []
  try {
    const start = startDate.value.format('YYYY-MM-DD')
    const end = endDate.value.format('YYYY-MM-DD')

    if (state.dataType === 'regas-costs') {
      if (state.priceReleasesFilter === 'latest') {
        const result = await accessStore.fetchLatestSparkRCsv(state.vesselSize, state.unit)
        columns = result.columns
        rows = result.rows
      } else if (state.priceReleasesFilter === 'date-range') {
        const result = await accessStore.fetchAllSparkRCsv(
          state.vesselSize, state.unit,
          start, end
        )
        columns = result.columns
        rows = result.rows
      }
    } else if (state.dataType === 'des-hub-netbacks') {
      const result =
        state.priceReleasesFilter === 'latest'
          ? await accessStore.fetchLatestDesHubNetbacksCsv(state.unit)
          : await accessStore.fetchDesHubNetbacksCsv(state.unit, start, end)
      columns = result.columns
      rows = result.rows
    }

    pasteTableOntoExcel(columns, rows)
  } catch (err: unknown) {
    if (err instanceof Error) {
      state.errors.push(err.message)
    } else {
      state.errors.push('An unknown error occurred')
    }
  } finally {
    // Show error modal if there are errors
    if (state.errors.length > 0) {
      state.showErrorModal = true
    }
    state.submitting = false
  }
}

/**
 * Optimized function to paste table data into Excel with performance improvements
 * based on Microsoft Office Add-ins performance best practices:
 * 
 * 1. Suspends screen updating and calculations to prevent flicker and improve performance
 * 2. Batches all data operations and reduces sync calls to minimum
 * 3. Creates data in array first, then creates table over populated range (more efficient)
 * 4. Optimizes range operations by calculating ranges once and reusing
 * 5. Handles large datasets with payload size monitoring
 * 6. Improves error handling and type safety
 */
function pasteTableOntoExcel(columns: string[], rows: string[][]) {
  try {
    if (!window.Excel) {
      console.warn('Excel is not loaded')
      return
    }

    // Performance optimization: Check for large datasets and handle payload size limits
    const COLUMNS_COUNT = columns.length
    const ROWS_COUNT = rows.length
    const totalCells = ROWS_COUNT * COLUMNS_COUNT

    if (totalCells > MAX_CELLS_PER_OPERATION) {
      console.warn(`Large dataset detected: ${totalCells} cells. Consider splitting into smaller batches.`)
    }

    window.Excel.run(async (context) => {
      // Performance optimization: Suspend screen updating and calculations
      const app = context.workbook.application
      app.suspendScreenUpdatingUntilNextSync()
      app.suspendApiCalculationUntilNextSync()

      const workbook = context.workbook
      const sheet = workbook.worksheets.getActiveWorksheet()
      const activeRange = workbook.getSelectedRange()
      const firstCell = activeRange.getCell(0, 0)

      // Load all necessary properties in one batch
      sheet.load('tables')
      activeRange.load('address')
      await context.sync()

      const existingTableCollection = sheet.tables
      const existingTables = existingTableCollection.items

      // Performance optimization: Calculate ranges once and reuse
      const tableDataRange = firstCell.getAbsoluteResizedRange(
        ROWS_COUNT + 1, // +1 for header row
        COLUMNS_COUNT
      )

      // Clear existing tables that intersect with our new table range
      if (existingTables && existingTables.length > 0) {
        const tablesToDelete: string[] = []
        const tableIntersections: { table: Excel.Table; intersection: Excel.Range }[] = []
        
        // Create intersection objects for all existing tables
        for (const existingTable of existingTables) {
          const range = existingTable.getRange()
          const intersection = range.getIntersectionOrNullObject(tableDataRange)
          
          // Load the intersection's address to check if it exists after sync
          intersection.load('address')
          tableIntersections.push({ table: existingTable, intersection })
        }
        
        await context.sync()
        
        // Check which intersections actually exist and collect tables to delete
        for (const { table, intersection } of tableIntersections) {
          if (!intersection.isNullObject) {
            tablesToDelete.push(table.name)
          }
        }
        
        // Delete intersecting tables in batch
        for (const tableName of tablesToDelete) {
          try {
            sheet.tables.getItem(tableName).delete()
          } catch (err) {
            state.errors.push(`Error deleting table ${tableName}: ${JSON.stringify(err)}`)
          }
        }
        
        if (tablesToDelete.length > 0) {
          await context.sync()
        }
      }

      // Performance optimization: Create data array first, then create table over the range
      const allTableData = [columns, ...rows]
      
      // Set all data at once using range.values (more efficient than adding rows individually)
      tableDataRange.values = allTableData

      // Create table over the populated range
      const TABLE_NAME = 'DataTable' + generateId()
      const dataTable = sheet.tables.add(tableDataRange, true /* hasHeaders */)
      dataTable.name = TABLE_NAME

      // Performance optimization: Batch all styling operations with minimal range calls
      const headerRange = dataTable.getHeaderRowRange()
      const dataRange = dataTable.getDataBodyRange()
      
      // Apply table style first (most efficient)
      dataTable.style = 'TableStyleLight1'
      
      // Batch all header formatting in one object
      headerRange.format.fill.color = '#FFC217'
      const headerFont = headerRange.format.font
      headerFont.color = '#000000'
      headerFont.size = 12
      headerFont.name = 'Mont'
      headerFont.bold = true

      // Batch all data formatting in one object  
      dataRange.format.fill.color = '#FFFFFF'
      const dataFont = dataRange.format.font
      dataFont.color = '#000000'
      dataFont.name = 'Roboto'
      dataFont.bold = false

      // Auto-fit columns and rows if supported (do this once at the end)
      if (Office.context.requirements.isSetSupported('ExcelApi', '1.2')) {
        const usedRange = sheet.getUsedRange()
        const usedRangeFormat = usedRange.format
        usedRangeFormat.autofitColumns()
        usedRangeFormat.autofitRows()
      }

      sheet.activate()
      
      // Single sync call at the end for all operations
      await context.sync()
    }).catch(async (error) => {
      console.error('Error in pasteTableOntoExcel Excel.run:', error)
      // If Excel.run fails, the suspensions should be automatically cleared
      throw error
    })

    // analyticsTrack('RUN_EXCEL_ADDIN', {
    //   contracts: selectedContracts.map((c) => c.id),
    //   priceReleasesFilter: state.priceReleasesFilter,
    // })
  } catch (err: any) {
    state.errors.push(err.message)
  } finally {
    state.submitting = false
  }
}

const router = useRouter()

function handleBreadcrumbInput(value: string) {
  router.push({ name: value })
}
</script>

<template>
  <Loader :modelValue="mainStore.isLoading">
    <div class="access-page p-5 space-y-10">
      <section class="space-y-6">
        <PageHeader :title="`Get Access Data`" />
        <SparkBreadcrumb
          :config="[
            { name: 'Products', icon: 'home-filled', value: ROUTE_NAMES.HOME },
            {
              name: 'Access',
              icon: 'accessLogo',
              styleClasses: `bg-access-500 text-black`
            }
          ]"
          @input="handleBreadcrumbInput"
        />
      </section>
      
      <a-form layout="vertical">
        <a-form-item :colon="false" label="Select Dataset">
          <div class="grid grid-cols-2 gap-3">
            <div
              v-for="opt in dataTypeOptions"
              :key="opt.value"
              class="data-type-card"
              :class="{ 'data-type-card--selected': state.dataType === opt.value }"
              @click="state.dataType = opt.value"
            >
              <div class="data-type-card__content">
                <div class="data-type-card__header">
                  <span class="data-type-card__title">{{ opt.text }}</span>
                  <a-popover placement="top" trigger="hover">
                    <template #content>
                      <div class="max-w-sm text-base">
                        <div class="font-semibold mb-2 text-lg">Accessibility</div>
                        <div class="mb-3">
                          <span class="font-medium text-green-600">Premium:</span>
                          <span class="ml-1">Full access</span>
                        </div>
                        <div>
                          <span class="font-medium text-amber-600">Trial:</span>
                          <ul class="list-disc list-inside mt-2 space-y-2 text-gray-700">
                            <li>Only last 2 weeks of data</li>
                            <li>
                              Fields hidden:
                              <span class="text-gray-600">{{ opt.trialHiddenFields.join(', ') }}</span>
                            </li>
                            <li>
                              Additional field: <code class="bg-gray-100 px-1 rounded">otherCostsTrialOnly</code>
                            </li>
                            <li class="text-gray-600 italic text-sm">
                              otherCostsTrialOnly = fuelGasLossesGasInKind + entryCapacity + entryVariable + emissions + power
                            </li>
                          </ul>
                        </div>
                      </div>
                    </template>
                    <InfoCircleOutlined class="data-type-card__info-icon" @click.stop />
                  </a-popover>
                </div>
                <span class="data-type-card__description">{{ opt.description }}</span>
              </div>
            </div>
          </div>
        </a-form-item>
        <a-form-item v-if="state.dataType === 'regas-costs'" :colon="false" label="Vessel Type">
          <a-radio-group
            v-model:value="state.vesselSize"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in vesselSizeOptions"
              :value="opt.value"
              :key="opt.value"
              >{{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item :colon="false" label="Price Unit">
          <a-radio-group
            v-model:value="state.unit"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in unitOptions"
              :value="opt.value"
              :key="opt.value"
              >{{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item :colon="false" label="Releases">
          <a-radio-group
            class="w-full"
            v-model:value="state.priceReleasesFilter"
            size="small"
          >
            <a-radio-button
              v-for="opt in priceReleasesOptions"
              :value="opt.value"
              :key="opt.value"
              >{{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <div
          v-if="state.priceReleasesFilter === 'date-range'"
        >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <a-form-item :colon="false" label="Start Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="startDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
            <div>
              <a-form-item :colon="false" label="End Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="endDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
          </div>
          <p class="text-sm text-amber-600 flex items-center gap-1 mb-4">
            <InfoCircleOutlined />
            <span>Maximum date range is 1 year</span>
          </p>
        </div>
        <div>
          <SparkButton
            class="w-full"
            size="lg"
            color="access"
            :loading="state.submitting"
            @click="loadData"
            >Insert data into selected cell
          </SparkButton>
        </div>
      </a-form>
      
      <ErrorModal
        v-model:visible="state.showErrorModal"
        title="Some data could not be retrieved"
        description="The following issues were encountered during data retrieval."
        :errors="state.errors"
      />
    </div>
  </Loader>
</template>
<style lang="scss">
.data-type-card {
  @apply p-3 rounded-lg border-2 border-gray-200 cursor-pointer transition-all duration-200;
  
  &:hover {
    @apply border-access-400 bg-access-50;
  }
  
  &--selected {
    @apply border-access-500 bg-access-50;
    
    &:hover {
      @apply border-access-500;
    }
  }
  
  &__content {
    @apply flex flex-col gap-0.5;
  }
  
  &__header {
    @apply flex items-center justify-between gap-2;
  }
  
  &__title {
    @apply font-semibold text-base text-gray-900 leading-tight;
  }
  
  &__info-icon {
    @apply text-gray-700 hover:text-gray-600 transition-colors text-sm;
  }
  
  &__description {
    @apply text-sm text-gray-600 leading-tight;
  }
}

.access-page {
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: #FFC217 !important;
    border-color: #FFC217 !important;
    color: #000000 !important;

    span {
      color: #000000 !important;
    }

    &:hover {
      background: #DEA400 !important;
      border-color: #DEA400 !important;
      color: #000000 !important;
    }

    &::before {
      background-color: #FFC217 !important;
    }
  }
}

.theme-table {
  thead th {
    @apply py-2 border-b-1 border-black;
  }

  tbody td {
    @apply py-1;
  }
}
</style>
